import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useAllowedImgExtensions } from '@features/offers/hooks/useAllowedImgExtensions';
import {
  Controller,
  DatePickerController,
  InputNumberController,
  RadioController,
} from '@organisms';
import { Button, Col, Flex, Form, Row, Space, Typography } from 'antd';
import {
  Control,
  Controller as ControllerHookForm,
  useFieldArray,
} from 'react-hook-form';

import { InputColor } from 'src/app/framework/atoms/InputColor/InputColor.atom';
import { toFieldStatus } from 'src/app/utils/toFieldStatus';
import { UploadImage } from 'src/features/Funnel/Components/UploadImage';

import { TypeCounter } from '../../../interfaces/offersInterface';

interface FormCounterProps {
  typeCounter: TypeCounter;
  bgCounter: string;
  multiCounterBgCounter: string;
  control: Control<any>;
}

export const FormCounter = ({
  control,
  typeCounter,
  bgCounter,
  multiCounterBgCounter,
}: FormCounterProps) => {
  const { Text } = Typography;
  const { allowedImgExtensions } = useAllowedImgExtensions([
    'jpg',
    'jpeg',
    'png',
    'gif',
  ]);
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'counter.multiCounter.schedules',
  });

  return (
    <Flex
      vertical
      style={{ width: '100%', minHeight: '610px', paddingBlockEnd: '24px' }}
    >
      <Row style={{ display: 'flex', paddingBlockStart: '24px' }}>
        <Col>
          <Text
            style={{
              width: '100%',
              lineHeight: '28px',
              paddingInlineEnd: '16px',
            }}
          >
            Tipo do contador
          </Text>
          <RadioController
            control={control}
            name='counter.typeCounter'
            defaultValue={typeCounter}
            options={[
              { label: 'Ofertas', value: 'offerCounter' },
              {
                label: 'Visita única',
                value: 'uniqueVisitCounter',
              },
              { label: 'Múltiplo', value: 'multiCounter' },
            ]}
          />
        </Col>
      </Row>
      {typeCounter === 'offerCounter' && (
        <>
          <Row gutter={[24, 24]} style={{ paddingBlockStart: '24px' }}>
            <Col xs={24} lg={12}>
              <UploadImage
                style={{ width: '100%' }}
                tooltip={{
                  title: () => (
                    <Typography.Text>
                      Imagem da esquerda, Logotipo Web - 170 x 100 pixels (
                      {allowedImgExtensions.allowedExtensionsText})
                    </Typography.Text>
                  ),
                }}
                control={control}
                name={`counter.offerCounter.leftImg`}
                label='Imagem da esquerda'
                required={false}
                allowedExtensions={allowedImgExtensions.allowedExtensions}
                includeResourceBaseUrl
              />
            </Col>
            <Col xs={24} lg={12}>
              <UploadImage
                style={{ width: '100%' }}
                tooltip={{
                  title: () => (
                    <Typography.Text>
                      Imagem da esquerda, Logotipo Responsivo - 91 x 52 pixels (
                      {allowedImgExtensions.allowedExtensionsText})
                    </Typography.Text>
                  ),
                }}
                control={control}
                name={`counter.offerCounter.leftImgMobile`}
                label='Imagem da esquerda Responsivo'
                required={false}
                allowedExtensions={allowedImgExtensions.allowedExtensions}
                includeResourceBaseUrl
              />
            </Col>
            <Col xs={24} lg={12}>
              <UploadImage
                style={{ width: '100%' }}
                tooltip={{
                  title: () => (
                    <Typography.Text>
                      Imagem da direita, Assets Web - 305 x 100 pixels (
                      {allowedImgExtensions.allowedExtensionsText})
                    </Typography.Text>
                  ),
                }}
                control={control}
                name={`counter.offerCounter.rightImg`}
                label='Imagem da direita'
                required={false}
                allowedExtensions={allowedImgExtensions.allowedExtensions}
                includeResourceBaseUrl
              />
            </Col>
            <Col xs={24} lg={12}>
              <UploadImage
                style={{ width: '100%' }}
                tooltip={{
                  title: () => (
                    <Typography.Text>
                      Imagem da esquerda, Assets Responsivo - 163 x 110 pixels (
                      {allowedImgExtensions.allowedExtensionsText})
                    </Typography.Text>
                  ),
                }}
                control={control}
                name={`counter.offerCounter.rightImgMobile`}
                label='Imagem da direita Responsivo'
                required={false}
                allowedExtensions={allowedImgExtensions.allowedExtensions}
                includeResourceBaseUrl
              />
            </Col>
          </Row>
          <Row gutter={[24, 24]} style={{ paddingBlockStart: '20px' }}>
            <Col xs={24} lg={12}>
              <Form.Item
                label='Cor de fundo'
                required
                style={{ width: '100%' }}
              >
                <ControllerHookForm
                  control={control}
                  name={`counter.offerCounter.bgCounter`}
                  render={({ field, fieldState }) => {
                    const { status, help } = toFieldStatus(fieldState.error);
                    return (
                      <Form.Item validateStatus={status} help={help}>
                        <InputColor
                          aria-label='Cor de fundo'
                          defaultValue={bgCounter}
                          onBlur={field.onBlur}
                          onValueChange={value => field.onChange(value)}
                        />
                      </Form.Item>
                    );
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {typeCounter === 'uniqueVisitCounter' && (
        <>
          <Row
            gutter={[24, 24]}
            style={{
              paddingBlockStart: '24px',
            }}
          >
            <Col xs={24} lg={12}>
              <Controller
                control={control}
                type='text'
                name={`counter.uniqueVisitCounter.timeoutRedirectUrl`}
                defaultValue={''}
                placeholder='Digite a url de redirecionamento'
                label='Url de redirecionamento (após timeout)'
                required
              />
            </Col>{' '}
            <Col xs={24} lg={12}>
              <Controller
                control={control}
                type='text'
                name={`counter.uniqueVisitCounter.countdownPhrase`}
                defaultValue={''}
                placeholder='Digite a frase a ser exibida'
                label='Frase do contador'
                required
                tooltip={{
                  title: () => (
                    <Typography.Text>
                      {
                        // eslint-disable-next-line no-template-curly-in-string
                        'Utilize ${value} para determinar o local do valor do desconto.'
                      }
                      <br />
                      Ex.:{' '}
                      {
                        // eslint-disable-next-line no-template-curly-in-string
                        'Aproveite ${value} de desconto!'
                      }
                    </Typography.Text>
                  ),
                }}
              />
            </Col>
          </Row>
          <Row
            gutter={[24, 24]}
            style={{
              paddingBlockStart: '24px',
            }}
          >
            <Col xs={24} lg={12}>
              <Form.Item label='Definição de tempo' required>
                <Space.Compact block>
                  <div style={{ flex: 1 }}>
                    <InputNumberController
                      control={control}
                      name='counter.uniqueVisitCounter.timerMinute'
                      placeholder='minutos'
                      min={0}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <InputNumberController
                      control={control}
                      name='counter.uniqueVisitCounter.timerSecond'
                      placeholder='segundos'
                      min={0}
                      max={59}
                      style={{ width: '100%' }}
                    />
                  </div>
                </Space.Compact>
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <InputNumberController
                control={control}
                type='number'
                name={`counter.uniqueVisitCounter.cacheExpirationTime`}
                defaultValue={''}
                placeholder='Digite o tempo em que o cache deve durar'
                label='Expiração do cache (min)'
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
        </>
      )}
      {typeCounter === 'multiCounter' && (
        <>
          <Row gutter={[24, 24]} style={{ paddingBlockStart: '24px' }}>
            <Col xs={24} lg={12}>
              <UploadImage
                style={{ width: '100%' }}
                tooltip={{
                  title: () => (
                    <Typography.Text>
                      Imagem da esquerda, Logotipo Web - 170 x 100 pixels (
                      {allowedImgExtensions.allowedExtensionsText})
                    </Typography.Text>
                  ),
                }}
                control={control}
                name={`counter.multiCounter.leftImg`}
                label='Imagem da esquerda'
                required={false}
                allowedExtensions={allowedImgExtensions.allowedExtensions}
                includeResourceBaseUrl
              />
            </Col>
            <Col xs={24} lg={12}>
              <UploadImage
                style={{ width: '100%' }}
                tooltip={{
                  title: () => (
                    <Typography.Text>
                      Imagem da esquerda, Logotipo Responsivo - 91 x 52 pixels (
                      {allowedImgExtensions.allowedExtensionsText})
                    </Typography.Text>
                  ),
                }}
                control={control}
                name={`counter.multiCounter.leftImgMobile`}
                label='Imagem da esquerda Responsivo'
                required={false}
                allowedExtensions={allowedImgExtensions.allowedExtensions}
                includeResourceBaseUrl
              />
            </Col>
            <Col xs={24} lg={12}>
              <UploadImage
                style={{ width: '100%' }}
                tooltip={{
                  title: () => (
                    <Typography.Text>
                      Imagem da direita, Assets Web - 305 x 100 pixels (
                      {allowedImgExtensions.allowedExtensionsText})
                    </Typography.Text>
                  ),
                }}
                control={control}
                name={`counter.multiCounter.rightImg`}
                label='Imagem da direita'
                required={false}
                allowedExtensions={['mp4', 'jpg', 'jpeg', 'png', 'gif']}
                includeResourceBaseUrl
              />
            </Col>
            <Col xs={24} lg={12}>
              <UploadImage
                style={{ width: '100%' }}
                tooltip={{
                  title: () => (
                    <Typography.Text>
                      Imagem da esquerda, Assets Responsivo - 163 x 110 pixels (
                      {allowedImgExtensions.allowedExtensionsText})
                    </Typography.Text>
                  ),
                }}
                control={control}
                name={`counter.multiCounter.rightImgMobile`}
                label='Imagem da direita Responsivo'
                required={false}
                allowedExtensions={allowedImgExtensions.allowedExtensions}
                includeResourceBaseUrl
              />
            </Col>
          </Row>
          <Row gutter={[24, 24]} style={{ paddingBlockStart: '20px' }}>
            <Col xs={24} lg={12}>
              <Form.Item
                label='Cor de fundo'
                required
                style={{ width: '100%' }}
              >
                <ControllerHookForm
                  control={control}
                  name={`counter.multiCounter.bgCounter`}
                  render={({ field, fieldState }) => {
                    const { status, help } = toFieldStatus(fieldState.error);
                    return (
                      <Form.Item validateStatus={status} help={help}>
                        <InputColor
                          aria-label='Cor de fundo'
                          defaultValue={multiCounterBgCounter}
                          onValueChange={value => field.onChange(value)}
                          onBlur={field.onBlur}
                        />
                      </Form.Item>
                    );
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* AGENDAMENTOS DINÂMICOS */}
          <Row style={{ paddingBlockStart: '20px' }}>
            <Col span={24}>
              <Typography.Title level={5}>Agendamentos</Typography.Title>
              <>
                {fields.map((item, index) => (
                  <Row
                    gutter={24}
                    key={item.id}
                    style={{
                      marginBottom: '16px',
                      alignItems: 'center',
                    }}
                  >
                    <Col xs={24} md={11}>
                      <DatePickerController
                        name={`counter.multiCounter.schedules.${index}.startTime`}
                        showTime={true}
                        label='Selecione a data de ativação do contador'
                        format='DD/MM/YYYY HH:mm:ss'
                        control={control}
                        required
                      />
                    </Col>

                    <Col xs={24} md={11}>
                      <DatePickerController
                        name={`counter.multiCounter.schedules.${index}.endTime`}
                        showTime={true}
                        label='Selecione a data de expiração do contador'
                        format='DD/MM/YYYY HH:mm:ss'
                        control={control}
                        required
                      />
                    </Col>

                    <Col
                      xs={24}
                      md={2}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '12px',
                      }}
                    >
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(index)}
                      />
                    </Col>
                  </Row>
                ))}

                <Button
                  type='dashed'
                  onClick={() => append({ startTime: '', endTime: '' })}
                  icon={<PlusOutlined />}
                  block
                >
                  Adicionar agendamento
                </Button>
              </>
            </Col>
          </Row>
        </>
      )}
    </Flex>
  );
};
